import {
  Address as PBAddress,
  Address_Country as Country,
} from "gen/proto/address/models_pb"

// nonNilCountry normalizes COUNTRY_UNDEFINED to COUNTRY_US,
// matching Go's Address_Country.NonNil().
export function nonNilCountry(c: Country): Country {
  return c === Country.CA ? Country.CA : Country.US
}

export class Address {
  private address: PBAddress

  private constructor(pbAddress: PBAddress) {
    this.address = pbAddress
  }

  static fromPB(pbAddress: PBAddress): Address {
    return new Address(pbAddress)
  }

  toPB(): PBAddress {
    return PBAddress.create(this.address)
  }

  get formatLines(): string[] {
    if (this.address.line1 === "") {
      return []
    }

    const firstLine =
      this.address.line2 === ""
        ? this.address.line1
        : `${this.address.line1} ${this.address.line2}`

    // A partial address must not leave the separators of the parts it is
    // missing, so each part joins only once it has something to contribute.
    const region = [this.address.state, this.address.postalCode]
      .filter(Boolean)
      .join(" ")
    const cityLine = [this.address.city, region].filter(Boolean).join(", ")

    return [firstLine, cityLine].filter(Boolean)
  }

  get format(): string {
    return this.formatLines.join("\n")
  }

  // streetAndCity returns the street line and the city, for dense single-line
  // display where state and postal code would not fit. Callers pick the
  // separator. Derived from formatLines so the two agree on what counts as an
  // address.
  get streetAndCity(): string[] {
    const [street] = this.formatLines
    if (!street) {
      return []
    }

    return [street, this.address.city].filter((part) => part !== "")
  }

  get isValid(): boolean {
    return (
      !!this.address.line1 &&
      !!this.address.city &&
      !!this.address.state &&
      !!this.address.postalCode
    )
  }

  // Country is ignored: a form seeds it from the company, so an address
  // carrying nothing but a country is one the user never filled out.
  get isEmpty(): boolean {
    return (
      !this.address.line1 &&
      !this.address.line2 &&
      !this.address.city &&
      !this.address.state &&
      !this.address.postalCode
    )
  }

  static eqFromPB(addressA?: PBAddress, addressB?: PBAddress): boolean {
    if (!addressA && !addressB) {
      return true
    }

    if (!addressA) {
      return false
    } else if (!addressB) {
      return false
    } else {
      return this.fromPB(addressA).eq(addressB)
    }
  }

  eq(other: PBAddress): boolean {
    return (
      this.address.line1 === other.line1 &&
      this.address.line2 === other.line2 &&
      this.address.city === other.city &&
      this.address.state === other.state &&
      this.address.postalCode === other.postalCode &&
      nonNilCountry(this.address.country) === nonNilCountry(other.country)
    )
  }
}
